import React, { FC } from 'react';
import TimePicker, { TimePickerValue } from 'react-time-picker';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import MaterialTable, { Column } from 'material-table';
import { Paper, makeStyles, Grid } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import EditIcon from '@material-ui/icons/Edit';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';

import DateCell from '../common/DateCell';
import Dropdown from '../common/Dropdown/Dropdown';
import SimpleTextInfo from '../common/SimpleTextInfo';

import { getSportShortTextDropdown, getSportTeamDropdown, getSportTitleDropdown } from '../highlights/Periods';
import { useLanguageContext } from '../languages';
import theme from '../../theme';
import { getLocalTimezone } from '../../utils/dateTransform';
import { Period, Team } from '../../utils/types';
import { isRowValid } from '../../utils/validation';

import '../common/react-time-picker.css';

dayjs.extend(duration);

const unknownValue = '';

const useStyles = makeStyles((theme) => ({
	dropdowns: {
		minWidth: '125px'
	}
}));

const Periods: FC<PeriodsProps> = (props) => {
	const { hideDetails, periods, title, sport, teams, leagueId, onWatchClip, onRowUpdate, onRowDelete, onEdit } = props;

	const classes = useStyles();
	const { tableLocalization } = useLanguageContext();

	const rowStyle = (rowData: EditablePeriod): CSSProperties => {
		if (!isRowValid(sport ?? '', toPeriod(rowData))) {
			return { color: theme.palette.error.main };
		} else {
			return {};
		}
	};

	const listOfTeams: { [key: string]: string } = {};
	teams &&
		teams.forEach((team) => {
			listOfTeams[team.id] = team.name!;
		});

	const toPeriod = (editablePeriod: EditablePeriod): Period => {
		const { firstSegment, secondSegment, thirdSegment, fourthSegment, data } = editablePeriod;
		if (data.startGameTime) {
			data.startGameTime.segmentId = [firstSegment, secondSegment, thirdSegment, fourthSegment].join('.');
			data.startGameTime.segment = parseInt(firstSegment);
		}
		return data;
	};

	const handlePeriodUpdate = async (newData: EditablePeriod, oldData?: EditablePeriod): Promise<unknown> => {
		if (onRowUpdate) {
			const newPeriod = toPeriod(newData);
			return await onRowUpdate(newPeriod, oldData?.data);
		}
	};

	const handlePeriodDelete = async (oldPeriod: EditablePeriod): Promise<unknown> => {
		if (onRowDelete) {
			return await onRowDelete(oldPeriod.data);
		}
	};

	// prettier-ignore
	const actions = [
        ...(onWatchClip
            ? [
                {
                    icon: PlayCircleFilledIcon,
                    tooltip: 'View Clip',
                    onClick: (event: unknown, rowData: unknown): void => {
                        const detailedPeriod = rowData as EditablePeriod;
                        const clipDuration = dayjs.duration(
                            dayjs(
                                detailedPeriod.data.source.endTime ?? ''
                            ).diff(
                                dayjs(
                                    detailedPeriod.data.source.startTime ?? ''
                                )
                            )
                        );
                        onWatchClip(
                            detailedPeriod.data.source.startTime!,
                            clipDuration.asMilliseconds() / 1000,
                            detailedPeriod.data.source.channelId!
                        );
                    },
                },
            ]
            : []),
        ...(onEdit
            ? [
                {
                    icon: EditIcon,
                    tooltip: 'Edit Clips',
                    isFreeAction: true,
                    onClick: onEdit,
                },
            ]
            : []),
    ];

	const renderSegmentIdSection = (rowData: Period, sectionPosition: number): string => {
		const { startGameTime } = rowData;
		if (startGameTime) {
			const { segmentId } = startGameTime;
			if (segmentId) {
				const segmentArray = segmentId.split('.');
				return segmentArray[sectionPosition] ?? unknownValue;
			} else {
				return unknownValue;
			}
		} else {
			return unknownValue;
		}
	};

	const detailsPanel = (rowData: EditablePeriod): JSX.Element => {
		const period = rowData.data;
		return (
			<Grid container direction='column' spacing={2} style={{ margin: theme.spacing(2) }}>
				<SimpleTextInfo label='Play ID' value={period.playId || 'Not specified'} compact />
			</Grid>
		);
	};

	const getSportColumns = (): Column<EditablePeriod>[] => {
		const sportLower = sport?.toLowerCase();
		if (sport && sportLower === 'golf') {
			if (Object.keys(listOfTeams).length) {
				return [
					{
						title: 'Team',
						field: 'data.team.id',
						lookup: listOfTeams
					},
					{
						title: 'Round',
						field: 'firstSegment',
						type: 'string'
					},
					{
						title: 'Hole',
						field: 'secondSegment',
						type: 'string'
					},
					{
						title: 'Yardage',
						field: 'thirdSegment',
						type: 'string'
					}
				];
			}

			return [
				{
					title: 'Round',
					field: 'firstSegment',
					type: 'string'
				},
				{
					title: 'Hole',
					field: 'secondSegment',
					type: 'string'
				}
			];
		} else if (sport && sportLower === 'football') {
			return [
				{
					title: 'Half',
					field: 'firstSegment',
					tooltip: '1: 1st Half, 2: 2nd Half, 3: ET 1st Half, 4: ET 2nd Half, 5: Penalty Shootout',
					lookup: {
						1: '1',
						2: '2',
						3: '3',
						4: '4',
						5: '5'
					}
				},
				{
					title: 'Minute',
					field: 'data.startGameTimeEditValue',
					tooltip: 'e.g. 54 - Leave blank for penalty shootout events',
					type: 'numeric'
				},
				{
					title: 'Team',
					field: 'data.team.id',
					lookup: listOfTeams
				}
			];
		} else if (sport && sportLower === 'cricket') {
			return [
				{
					title: 'Innings',
					field: 'firstSegment',
					type: 'string'
				},
				{
					title: 'Balls',
					field: 'secondSegment',
					type: 'string'
				},
				{
					title: 'Wickets',
					field: 'thirdSegment',
					type: 'string'
				},
				{
					title: 'Overs',
					field: 'fourthSegment',
					type: 'string'
				},
				{
					title: 'Team',
					field: 'data.team.id',
					lookup: listOfTeams
				}
			];
		} else if (sport && sportLower === 'f1') {
			return [
				{
					title: 'Lap',
					field: 'firstSegment'
				},
				{
					title: 'Team',
					field: 'data.team.id',
					editComponent: (props): JSX.Element => (
						<Dropdown
							autoSelect={true}
							freeSolo={true}
							onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
							getDropdownFunction={getSportTeamDropdown}
							type={props.rowData.data?.type ?? ''}
							sport={sport}
							editComponentProps={props}
							classes={classes}
						/>
					)
				}
			];
		} else if (sportLower && ['americanfootball', 'basketball'].includes(sportLower)) {
			return [
				{
					title: 'Quarter',
					field: 'firstSegment',
					tooltip: '1: 1st Quarter, 2: 2nd Quarter, 3: 3rd Quarter, 4: 4th Quarter, 5: Overtime 1, 6: Overtime 2, 7: Overtime 3, 8: Overtime 4',
					lookup: {
						1: '1',
						2: '2',
						3: '3',
						4: '4',
						5: '5',
						6: '6',
						7: '7',
						8: '8'
					}
				},
				{
					title: 'Minute',
					field: 'data.startGameTimeEditValue',
					tooltip: 'e.g. 12',
					type: 'numeric'
				},
				{
					title: 'Team',
					field: 'data.team.id',
					lookup: listOfTeams
				}
			];
		} else if (sportLower && sportLower === 'baseball') {
			return [
				{
					title: 'Inning',
					field: 'firstSegment',
					lookup: {
						'1t': 'Top of the 1st',
						'1b': 'Bottom of the 1st',
						'2t': 'Top of the 2nd',
						'2b': 'Bottom of the 2nd',
						'3t': 'Top of the 3rd',
						'3b': 'Bottom of the 3rd',
						'4t': 'Top of the 4th',
						'4b': 'Bottom of the 4th',
						'5t': 'Top of the 5th',
						'5b': 'Bottom of the 5th',
						'6t': 'Top of the 6th',
						'6b': 'Bottom of the 6th',
						'7t': 'Top of the 7th',
						'7b': 'Bottom of the 7th',
						'8t': 'Top of the 8th',
						'8b': 'Bottom of the 8th',
						'9t': 'Top of the 9th',
						'9b': 'Bottom of the 9th',
						'10t': 'Top of the 10th',
						'10b': 'Bottom of the 10th',
						'11t': 'Top of the 11th',
						'11b': 'Bottom of the 11th',
						'12t': 'Top of the 12th',
						'12b': 'Bottom of the 12th',
						'13t': 'Top of the 13th',
						'13b': 'Bottom of the 13th',
						'14t': 'Top of the 14th',
						'14b': 'Bottom of the 14th',
						'15t': 'Top of the 15th',
						'15b': 'Bottom of the 15th',
						'16t': 'Top of the 16th',
						'16b': 'Bottom of the 16th',
						'17t': 'Top of the 17th',
						'17b': 'Bottom of the 17th',
						'18t': 'Top of the 18th',
						'18b': 'Bottom of the 18th',
						'19t': 'Top of the 19th',
						'19b': 'Bottom of the 19th',
						'20t': 'Top of the 20th',
						'20b': 'Bottom of the 20th'
					}
				},
				{
					title: 'Team',
					field: 'data.team.id',
					lookup: listOfTeams
				},
				{
					title: 'Outs',
					field: 'data.sportData.outs',
					editable: 'never',
					filtering: false
				}
			];
		}
		return [];
	};

	// prettier-ignore
	return (
        <MaterialTable
            localization={tableLocalization}
            detailPanel={hideDetails ? undefined : detailsPanel}
            columns={[
                {
                    title: 'Title',
                    field: 'data.title',
                    editComponent: (props): JSX.Element => (
                        <Dropdown
                            autoSelect={true}
                            freeSolo={(sport?.toLocaleLowerCase() === 'golf') ? false : true}
                            onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
                            disableClearable={true}
                            getDropdownFunction={getSportTitleDropdown}
                            sport={sport}
                            leagueId={leagueId ?? undefined}
                            editComponentProps={props}
                            classes={classes}
                        />
                    ),
                },
                {
                    title: `Start Time (${getLocalTimezone()})`,
                    field: 'data.source.startTime',
                    cellStyle: { minWidth: 175 },
                    render: (row: EditablePeriod): JSX.Element => (
                        <DateCell
                            value={row.data.source?.startTime ?? ''}
                            showTimezone={false}
                        />
                    ),
                    editComponent: (props): JSX.Element => (
                        <TimePicker
                            name="startTime"
                            value={dayjs(props.value).toDate()}
                            format="HH:mm:ss"
                            maxDetail="second"
                            onChange={(value: TimePickerValue): void => {
                                if (typeof value === 'string') {
                                    const timeParts = value.split(':');
                                    const newValue = dayjs(props.value)
                                        .set('hour', Number(timeParts[0]))
                                        .set('minute', Number(timeParts[1]))
                                        .set('second', Number(timeParts[2]));
                                    props.onChange(newValue.toISOString());
                                }
                            }}
                            clearIcon={null}
                            disableClock={true}
                        />
                    ),
                },
                {
                    title: `End Time (${getLocalTimezone()})`,
                    field: 'data.source.endTime',
                    cellStyle: { minWidth: 175 },
                    render: (row: EditablePeriod): JSX.Element => (
                        <DateCell
                            value={row.data.source?.endTime ?? ''}
                            showTimezone={false}
                        />
                    ),
                    editComponent: (props): JSX.Element => (
                        <TimePicker
                            name="endTime"
                            value={dayjs(props.value).toDate()}
                            format="HH:mm:ss"
                            maxDetail="second"
                            onChange={(value: TimePickerValue): void => {
                                if (typeof value === 'string') {
                                    const timeParts = value.split(':');
                                    const newValue = dayjs(props.value)
                                        .set('hour', Number(timeParts[0]))
                                        .set('minute', Number(timeParts[1]))
                                        .set('second', Number(timeParts[2]));
                                    props.onChange(newValue.toISOString());
                                }
                            }}
                            clearIcon={null}
                            disableClock={true}
                        />
                    ),
                },
                {
                    title:
                        sport?.toLowerCase() === 'f1'
                            ? 'Position'
                            : sport?.toLowerCase() === 'cricket'
                                ? 'Runs'
                                : 'Score',
                    field: 'data.endScore',
                },
                ...getSportColumns(),
                {
                    title:
                        sport?.toLowerCase() === 'f1'
                            ? 'Driver name'
                            : 'Player name',
                    field: 'data.shortText',
                    editComponent: (props): JSX.Element => (
                        <Dropdown
                            autoSelect={true}
                            freeSolo={sport?.toLowerCase() !== 'f1'}
                            disableClearable={
                                sport?.toLowerCase() !== 'f1' &&
                                sport?.toLowerCase() !== 'cricket'
                            }
                            selectOnFocus={false}
                            onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
                            getDropdownFunction={getSportShortTextDropdown}
                            teamId={props.rowData.data?.team?.id}
                            sport={sport}
                            leagueId={leagueId ?? undefined}
                            editComponentProps={props}
                            classes={classes}
                        />
                    ),
                },
                {
                    title: 'Channel',
                    field: 'data.source.channelId',
                    editable: 'never',
                },
            ]}
            actions={actions}
            editable={{
                onRowDelete: onRowDelete ? handlePeriodDelete : undefined,
                onRowUpdate: onRowUpdate ? handlePeriodUpdate : undefined,
            }}
            data={periods.map((period) => {
                return {
                    data: period,
                    firstSegment: renderSegmentIdSection(period, 0),
                    secondSegment: renderSegmentIdSection(period, 1),
                    thirdSegment: renderSegmentIdSection(period, 2),
                    fourthSegment: renderSegmentIdSection(period, 3),
                };
            })}
            options={{ pageSize: 10, search: false, sorting: false, rowStyle, emptyRowsWhenPaging: false }}
            title={title ?? 'Video Clips'}
            components={{
                Container: (props): JSX.Element => (
                    <Paper {...props} data-testid="periods-table" />
                ),
            }}
        />
    );
};

type PeriodsProps = {
	periods: Period[];
	hideDetails?: boolean | null;
	title?: string;
	sport?: string | null;
	leagueId?: string | null;
	teams?: Team[] | null | undefined;
	onWatchClip?: (startTime: string, duration: number, channelId: string) => void;
	onRowUpdate?: (newData: Period, oldData?: Period) => Promise<unknown>;
	onRowDelete?: (data: Period) => Promise<unknown>;
	onEdit?: () => void;
};

type EditablePeriod = {
	data: Period;
	firstSegment: string;
	secondSegment: string;
	thirdSegment: string;
	fourthSegment: string;
	editorial?: boolean;
};

export const getEditablePeriod = (periods: Period[]): Period[] => {
	let periodItems: Period[] = [];
	if (periods) {
		periodItems = periods.map((item: Period) => {
			const newItem: Period = { ...item };
			if (item.startGameTime) {
				const regexp = /PT(\d+)M(\d*)S*/;
				const groups = regexp.exec(item.startGameTime.position);
				if (groups && groups[1].length > 0) {
					newItem.startGameTimeEditValue = parseInt(groups[1]);
				}
			}
			return newItem;
		});
	}
	return periodItems;
};

export default Periods;
